/**
 * Parsing helpers for the MarkLogic box-grid heatmap.
 *
 * Mirrors the logic of sensingcluesr::get_heatmap() (v1.1.0): the backend is
 * asked for a Cols x Rows raster over the exact bounding box we send, via
 *   /api/map/all/default/{0|1}/features?returnQuery=true&returnFeatures=false&heatmap=ColsxRows
 * and returns a lean payload:
 *   { metadata: { total, bounds }, boxes: [{ bounds, count }, ...] }
 * where `bounds` is a JSON string "[south, west, north, east]". Edge cells use
 * sentinel values (-90 / -180 / 90 / 180) that must be clamped to the raster's
 * actual outer bounds (metadata.bounds) - exactly what the R implementation
 * does before placing each count in its grid cell.
 *
 * The grid resolution is chosen client-side, before the request goes out, so
 * that the raster the backend lays over the requested bounding box already
 * comes out in square (ground-distance) cells - see `heatmapGridParam()`.
 * Because the backend grids over the exact box it was given, the response
 * never needs reshaping on the way back in: what arrives is what gets drawn.
 *
 * This module is entity-agnostic: the same helpers serve the Observations and
 * Tracks layers. Keep it maintained here in this single location.
 */

/**
 * Parses a bounds value that may arrive either as a JSON string
 * "[south, west, north, east]" or as an already-parsed array.
 * @param {string|Array} bounds
 * @returns {[number, number, number, number]|null} [south, west, north, east]
 */
export function parseBounds(bounds) {
  let arr = bounds;
  if (typeof bounds === 'string') {
    try {
      arr = JSON.parse(bounds);
    } catch (e) {
      return null;
    }
  }
  if (!Array.isArray(arr) || arr.length < 4) return null;
  const [south, west, north, east] = arr.map(Number);
  if ([south, west, north, east].some(v => !Number.isFinite(v))) return null;
  return [south, west, north, east];
}

/**
 * Converts a raw bounds value (JSON string or array, [south, west, north,
 * east] - see `parseBounds()`) into the {north, east, south, west} shape
 * used by `heatmapGridParam()`. Used to turn a backend-reported
 * `metadata.bounds` into something we can compute a grid ratio from.
 * @param {string|Array} bounds
 * @returns {{north:number,east:number,south:number,west:number}|null}
 */
export function metadataBoundsToObject(bounds) {
  const parsed = parseBounds(bounds);
  if (!parsed) return null;
  const [south, west, north, east] = parsed;
  return { north, east, south, west };
}

/**
 * Turns the MarkLogic heatmap response into a GeoJSON FeatureCollection of
 * rectangular grid cells, one Feature per box returned by the backend.
 * Because the request already asked for a grid resolution matched to the
 * ground aspect ratio of the queried bounding box (see `heatmapGridParam()`),
 * every returned box is already a square patch of ground - this function
 * just needs to turn each one into a polygon, no reshaping required.
 *
 * Each output cell carries:
 *   - properties.count     number of results in the cell
 *   - properties.intensity count normalised to 0..1 against the busiest cell,
 *                           used to drive the fill colour ramp so the palette
 *                           adapts to whatever is currently in view.
 *
 * @param {object} response - the raw { metadata, boxes } payload.
 * @returns {{geojson: {type: string, features: Array}, total: number, maxCount: number}}
 */
export function parseHeatmapResponse(response) {
  const empty = {
    geojson: { type: 'FeatureCollection', features: [] },
    total: 0,
    maxCount: 0
  };
  if (!response || response.isError) return empty;

  const metadata = response.metadata || {};
  const total = Number(metadata.total) || 0;
  const boxes = Array.isArray(response.boxes) ? response.boxes : [];
  if (total <= 0 || boxes.length === 0) return { ...empty, total };

  // Outer bounds used to clamp the backend's sentinel edge values
  // (-90/-180/90/180) to the raster's real outer extent. Fall back to the
  // whole world if the backend didn't echo usable bounds.
  const outer = parseBounds(metadata.bounds) || [-90, -180, 90, 180];
  const [outerSouth, outerWest, outerNorth, outerEast] = outer;

  let maxCount = 0;
  const features = [];
  boxes.forEach(box => {
    const parsed = parseBounds(box && box.bounds);
    if (!parsed) return;
    let [south, west, north, east] = parsed;
    if (west === -180) west = outerWest;
    if (east === 180) east = outerEast;
    if (south === -90) south = outerSouth;
    if (north === 90) north = outerNorth;

    const count = Number(box.count) || 0;
    if (count > maxCount) maxCount = count;
    features.push({
      type: 'Feature',
      properties: { count },
      geometry: {
        type: 'Polygon',
        coordinates: [
          [
            [west, south],
            [east, south],
            [east, north],
            [west, north],
            [west, south]
          ]
        ]
      }
    });
  });

  const safeMax = maxCount > 0 ? maxCount : 1;
  features.forEach(f => {
    f.properties.intensity = f.properties.count / safeMax;
  });

  return { geojson: { type: 'FeatureCollection', features }, total, maxCount };
}

/**
 * Builds the `heatmap=ColsxRows` request parameter so the raster the backend
 * lays over `bounds` comes back in (visually) square cells, whatever the
 * bounding box's aspect ratio - no client-side padding or reshaping needed.
 *
 * The backend grids over the exact bounding box it is given, dividing it
 * into an evenly-spaced Cols x Rows raster in degrees. Longitude degrees
 * shrink towards the poles, so a cos(latitude) correction is used to compare
 * east-west and north-south ground distance fairly; the column/row count is
 * then biased so each cell covers equal ground distance in both directions.
 *
 * `resolution` sets the cell count along the *longer* ground axis; the
 * shorter axis gets proportionally fewer cells. The request bounds
 * themselves are never padded or stretched, so what's requested always
 * matches the visible map exactly.
 *
 * @param {{north:number,east:number,south:number,west:number}} bounds - the
 *   exact bounding box being requested (e.g. the current map viewport).
 * @param {number} [resolution=25] - cell count along the longer ground axis.
 * @returns {string} e.g. "18x25" (cols x rows)
 */
export function heatmapGridParam(bounds, resolution = 25) {
  const r = Math.max(1, Math.min(200, Math.round(Number(resolution) || 25)));
  const fallback = `${r}x${r}`;
  if (!bounds) return fallback;

  const { north, east, south, west } = bounds;
  if (![north, east, south, west].every(v => Number.isFinite(v))) {
    return fallback;
  }

  const latSpan = north - south;
  const lonSpan = east - west;
  if (!(latSpan > 0) || !(lonSpan > 0)) return fallback;

  const centreLat = (north + south) / 2;
  const cosLat = Math.max(0.01, Math.cos((centreLat * Math.PI) / 180));
  const groundLon = lonSpan * cosLat; // east-west ground extent
  const groundLat = latSpan; //          north-south ground extent

  let cols = r;
  let rows = r;
  if (groundLon > groundLat) {
    rows = Math.max(1, Math.round(r * (groundLat / groundLon)));
  } else if (groundLat > groundLon) {
    cols = Math.max(1, Math.round(r * (groundLon / groundLat)));
  }
  return `${cols}x${rows}`;
}
